import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/internal/operators/map";
import { AutomapperService } from "../../../core/automapper/automapper.service";
import { BASE_API_URL } from "../../../core/environment.tokens";
import { DocumentOcrMatch } from "../../../shared/document/document-page-viewer/document-ocr-match.model";
import { MemberChartM360Response } from "../member-v2/member-chart-response.model";
import { IChartHistoryResponse } from "./chart-history-response.model";
import { IChartMetadata } from "./charts-list-response.model";


@Injectable({
  providedIn: "root",
})
export class ChartService {

  constructor(@Inject(BASE_API_URL) private readonly baseApiUrl: string,
              private http: HttpClient,
              private automapper: AutomapperService) { }


  /**
   * POST /m360/charts/list
   * @param requestBody Request payload for charts list
   * @param filterPresent if any filters present
   */
  getChartsList(requestBody: any, filterPresent: boolean): Observable<MemberChartM360Response> {
    const endpoint = filterPresent ? "m360/charts/search" : "m360/charts/list";
    const url = `${this.baseApiUrl}${endpoint}`;
    const headers = new HttpHeaders({ "Content-Type": "application/json" });
    return this.http.post(url, requestBody, { headers }).pipe(
      map(this.automapper.curry("default", "MemberChartM360Response"))
    );
  }

  /**
   * GET /m360/charts/${chartId}
   * @param chartId
   */
  getChart(chartId: number): Observable<IChartMetadata> {
    const url = `${this.baseApiUrl}m360/charts/${chartId}`;
    const headers = new HttpHeaders({ "Content-Type": "application/json" });
    return this.http.get<IChartMetadata>(url, { headers });
  }

  /**
   * GET /m360/charts/${chartId}/presigned
   * @param chartId
   * @param startPage Optional starting page number
   * @param numPages Optional number of pages to fetch
   */
  getChartPresignedUrl(chartId: number, startPage?: number, numPages?: number): Observable<IChartMetadata> {
    const url = `${this.baseApiUrl}m360/charts/${chartId}/presigned`;
    let params = new HttpParams();
    if (startPage !== undefined) {
      params = params.set("startPage", startPage.toString());
    }
    if (numPages !== undefined) {
      params = params.set("numPages", numPages.toString());
    }

    const headers = new HttpHeaders({ "Content-Type": "application/json" });

    return this.http.get<IChartMetadata>(url, { headers, params });

  }

  submitChartDownloadRequest(chartId: number): Observable<string> {
    const url = `${this.baseApiUrl}m360/charts/${chartId}/start-download`;
    const headers = new HttpHeaders({ "Content-Type": "application/json" });
    return this.http.get<string>(url, { headers });
  }

  getPresignedDownloadUrl(jobId: string): Observable<string> {
    const url = `${this.baseApiUrl}m360/jobs/${jobId}/download`;
    const headers = new HttpHeaders({ "Content-Type": "application/json" });
    return this.http.get<string>(url, { headers });
  }

  getChartOCRMatches(chartId: string, searchPhrase: string): Observable<DocumentOcrMatch> {
    const url =
      `${this.baseApiUrl}m360/charts/${chartId}/ocrmatch?searchPhrase=${encodeURIComponent(searchPhrase)}`;
    return this.http.get(url).pipe(
      map(this.automapper.curry("default", "DocumentOcrMatch"))
    );
  }

  searchGroupNames(groupName: string): Observable<string[]> {
    const url = `${this.baseApiUrl}retrieval/searchList`;
    return this.http.post<{ groupName?: string }[]>(url, { groupName }).pipe(
      map(results => [...new Set(results.map(r => r.groupName).filter((n): n is string => !!n))])
    );
  }

  patchChartMetadata(chartId: number, payload: Partial<Pick<IChartMetadata, "providerName" | "groupName" | "dateOfServiceFrom" | "dateOfServiceTo" | "trackingNumber">>): Observable<void> {
    const url = `${this.baseApiUrl}m360/charts/${chartId}/metadata`;
    const headers = new HttpHeaders({ "Content-Type": "application/json" });
    return this.http.patch<void>(url, payload, { headers });
  }

  /**
   * GET /m360/charts/${chartId}/history
   */
  getChartHistory(chartId: number): Observable<IChartHistoryResponse> {
    const url = `${this.baseApiUrl}m360/charts/${chartId}/history`;
    return this.http.get<IChartHistoryResponse>(url);
  }
}
